import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AreaChart, Area, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { useAuth } from '../hooks/useAuth';
import { hvApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Star, QrCode, Plus, Loader2, Link as LinkIcon,
  MapPin, Zap, Lightbulb, Pencil, CheckCircle, AlertCircle,
  ArrowUpDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de as deLocale } from 'date-fns/locale';
import HVNav from '../components/HVNav';
import HVFooter from '../components/HVFooter';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

// ─── Plan helpers ─────────────────────────────────────────────────────────────
const PLAN_RANK = { free: 0, mvp: 0, standard: 1, premium: 2, enterprise: 3 };
function canCreateMultipleLinks(plan) {
  return (PLAN_RANK[plan] ?? 0) >= 1; // standard+
}

// ─── Name auto-suggest ───────────────────────────────────────────────────────
function nextLinkName(existing) {
  const nums = existing
    .map(l => { const m = l.name.match(/^Bewertung\s+(\d+)$/i); return m ? parseInt(m[1]) : 0; })
    .filter(Boolean);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return { name: `Bewertung ${next}`, slug: `bewertung-${next}` };
}

// ─── QR download helper ──────────────────────────────────────────────────────
function downloadQR(url, name) {
  const svgEl = document.getElementById(`qr-hidden-${name}`);
  if (!svgEl) return;
  const canvas = document.createElement('canvas');
  const size = 256;
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  const img = new Image();
  const svgData = new XMLSerializer().serializeToString(svgEl);
  img.onload = () => { ctx.drawImage(img, 0, 0, size, size); const a = document.createElement('a'); a.download = `qr-${name}.png`; a.href = canvas.toDataURL('image/png'); a.click(); };
  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
}

// ─── Sparkline ───────────────────────────────────────────────────────────────
function Sparkline({ data = [], labels = [] }) {
  const chartData = data.map((v, i) => ({ i, v, label: labels[i] || '' }));
  const total = data.reduce((a, b) => a + b, 0);
  const trend = data.length >= 2 ? data[data.length - 1] - data[0] : 0;
  const color = total === 0 ? '#94a3b8' : trend >= 0 ? '#22c55e' : '#f87171';
  const gradId = useRef(`spark-${Math.random().toString(36).slice(2)}`).current;

  return (
    <div className="w-24 h-9 shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.35} />
              <stop offset="95%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area
            type="monotone" dataKey="v" stroke={color} strokeWidth={1.5}
            fill={`url(#${gradId})`} dot={false} isAnimationActive={false}
          />
          <RechartsTooltip
            position={{ y: -62 }}
            allowEscapeViewBox={{ x: false, y: true }}
            content={({ active, payload }) =>
              active && payload?.length
                ? <div className="text-[10px] bg-background border border-border rounded px-2 py-1 shadow space-y-0.5 whitespace-nowrap">
                    <div className="text-muted-foreground">{payload[0].payload.label}</div>
                    <div className="font-medium">{payload[0].value} Bewertung{payload[0].value !== 1 ? 'en' : ''}</div>
                  </div>
                : null
            }
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function HVLinks() {
  const { user, loading: authLoading } = useAuth('hv_user');
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active'); // 'active' | 'inactive'
  const [createOpen, setCreateOpen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkSlug, setNewLinkSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const plan = user?.tenant?.billing?.plan || 'free';
  const canCreate = canCreateMultipleLinks(plan);
  const [liveSessions, setLiveSessions] = useState({});
  const [sortBy, setSortBy] = useState('count'); // count | positive | critical | avg

  useEffect(() => { if (user) loadLinks(); }, [user]);

  // Poll live sessions every 15s
  useEffect(() => {
    if (!user) return;
    const fetchLive = () =>
      hvApi.getLiveSessions()
        .then(r => setLiveSessions(r.data?.sessions || {}))
        .catch(() => {});
    fetchLive();
    const interval = setInterval(fetchLive, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const loadLinks = async () => {
    try {
      const response = await hvApi.getLinks();
      setLinks(response.data);
    } catch { toast.error('Fehler beim Laden der Links'); }
    finally { setLoading(false); }
  };

  // Open create dialog or upsell
  const handleCreateClick = () => {
    if (!canCreate && links.length >= 1) {
      setUpsellOpen(true);
      return;
    }
    // Pre-fill name + slug
    const { name, slug } = nextLinkName(links);
    setNewLinkName(name);
    setNewLinkSlug(slug);
    setCreateOpen(true);
  };

  const handleCreateLink = async () => {
    if (!newLinkName.trim()) return;
    setSubmitting(true);
    try {
      await hvApi.createLink({
        name: newLinkName,
        slug_hint: newLinkSlug || null,
      });
      toast.success('Link erstellt!');
      setCreateOpen(false);
      loadLinks();
    } catch { toast.error('Fehler beim Erstellen'); }
    finally { setSubmitting(false); }
  };

  const handleUpdateLink = async () => {
    if (!selectedLink) return;
    setSubmitting(true);
    try {
      await hvApi.updateLink(selectedLink.id, { name: newLinkName });
      toast.success('Link aktualisiert!');
      setEditOpen(false);
      setSelectedLink(null);
      loadLinks();
    } catch { toast.error('Fehler beim Aktualisieren'); }
    finally { setSubmitting(false); }
  };

  const handleToggleStatus = async (link) => {
    try {
      await hvApi.updateLink(link.id, { status: link.status === 'active' ? 'disabled' : 'active' });
      toast.success(link.status === 'active' ? 'Link deaktiviert' : 'Link aktiviert');
      loadLinks();
    } catch { toast.error('Fehler beim Aktualisieren'); }
  };

  const copyLink = (link) => {
    const url = `${window.location.origin}/r/${user?.tenant?.slug}/${link.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link kopiert!');
  };

  const openEditDialog = (link) => {
    setSelectedLink(link);
    setNewLinkName(link.name);
    setEditOpen(true);
  };

  // Slug helpers — keep ü/ä/ö/ß, strip everything else unsupported
  const toSlug = (val) =>
    val.toLowerCase()
       .replace(/\s+/g, '-')
       .replace(/[^a-zäöüß0-9-]/g, '')
       .replace(/-+/g, '-')
       .replace(/^-|-$/g, '')
       .slice(0, 50);

  const slugStatus = (slug) => {
    if (!slug) return { ok: false, msg: 'URL darf nicht leer sein.' };
    if (/^[a-zäöüß0-9][a-zäöüß0-9-]*[a-zäöüß0-9]$|^[a-zäöüß0-9]$/.test(slug))
      return { ok: true, msg: 'URL sieht gut aus.' };
    if (slug.startsWith('-') || slug.endsWith('-'))
      return { ok: false, msg: 'Keine führenden oder abschließenden Bindestriche.' };
    return { ok: false, msg: 'Nur Buchstaben (inkl. Umlaute), Zahlen und Bindestriche erlaubt.' };
  };

  // Duplicate name check
  const isDuplicateName = (val) =>
    links.some(l => l.name.trim().toLowerCase() === val.trim().toLowerCase());

  // Auto-update slug when name changes (only while un-touched)
  const [slugEdited, setSlugEdited] = useState(false);
  const onNameChange = (val) => {
    setNewLinkName(val);
    if (!slugEdited) setNewLinkSlug(toSlug(val));
  };

  // Totals across all links for percentage benchmarks
  const totalReviews = links.reduce((s, l) => s + (l.review_count || 0), 0);

  // Sort
  const sorted = [...links].sort((a, b) => {
    if (sortBy === 'positive') return (b.positive_count || 0) - (a.positive_count || 0);
    if (sortBy === 'critical') return (b.critical_count || 0) - (a.critical_count || 0);
    if (sortBy === 'avg') return (b.avg_rating || 0) - (a.avg_rating || 0);
    return (b.review_count || 0) - (a.review_count || 0); // default: count
  });

  const filtered = sorted
    .filter(l => tab === 'active' ? l.status === 'active' : l.status !== 'active');

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <HVNav />

      <main className="container-main py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Bewertungslinks</h1>
            <p className="text-muted-foreground">Link auswählen, um ihn zu bearbeiten und Statistiken einzusehen</p>
          </div>
          <Button onClick={handleCreateClick} data-testid="hv-create-link-button">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Link
          </Button>
        </div>

        {/* Sortierung */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground">Sortieren:</span>
          {[
            { key: 'count', label: 'Anzahl' },
            { key: 'positive', label: 'Positiv' },
            { key: 'critical', label: 'Kritisch' },
            { key: 'avg', label: 'Durchschnitt' },
          ].map(s => (
            <button key={s.key} onClick={() => setSortBy(s.key)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${sortBy === s.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-0 border-b border-border">
          {[
            { key: 'active', label: 'Aktive Links', count: links.filter(l => l.status === 'active').length },
            { key: 'inactive', label: 'Inaktive Links', count: links.filter(l => l.status !== 'active').length },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 ${tab === t.key ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Links list — single column, spacing below tabs */}
        <div className="mt-6" />
        {filtered.length === 0 ? (
          <Card className="card-shadow">
            <CardContent className="py-12 text-center">
              <LinkIcon className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {tab === 'active' ? 'Keine aktiven Links' : 'Keine inaktiven Links'}
              </h3>
              {tab === 'active' && (
                <Button onClick={handleCreateClick} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Ersten Link erstellen
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((link) => {
              const url = `${window.location.origin}/r/${user?.tenant?.slug}/${link.slug}`;
              return (
                <Card
                  key={link.id}
                  className="card-shadow cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/app/links/${link.id}`)}
                >
                  {/* Hidden QR for PNG download */}
                  <div className="sr-only">
                    <QRCodeSVG id={`qr-hidden-${link.slug}`} value={url} size={256} />
                  </div>

                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      {/* Left: name + meta */}
                      <div className="flex-1 min-w-0">
                        {/* Row 1: name + live dot + badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold">{link.name}</span>
                          {(liveSessions[link.slug] || 0) > 0 && (
                            <span className="flex items-center gap-1 text-[10px] font-medium text-green-600" title={`${liveSessions[link.slug]} Person(en) gerade auf dieser Seite`}>
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                              </span>
                              {liveSessions[link.slug]} Live
                            </span>
                          )}
                        </div>

                        {/* Row 1b: activity hint */}
                        {link.status === 'active' && (
                          <p className="text-xs text-muted-foreground mb-1.5">
                            {link.last_review_at
                              ? `Zuletzt bewertet ${formatDistanceToNow(new Date(link.last_review_at), { addSuffix: true, locale: deLocale })}`
                              : 'Noch keine Bewertungen eingegangen'}
                          </p>
                        )}

                        {/* Row 2: URL klickbar + QR-Button */}
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5 min-w-0">
                          <a href={url} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="truncate hover:text-primary hover:underline transition-colors">
                            {url}
                          </a>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/app/links/${link.id}?tab=einbetten`); }}
                            className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded border border-border hover:border-primary hover:text-primary transition-colors"
                            title="QR-Code / Einbetten"
                          >
                            <QrCode className="w-3 h-3" />
                            <span className="text-[10px]">QR</span>
                          </button>
                        </div>

                        {/* Row 3: % stats */}
                        {(link.review_count || 0) > 0 && (() => {
                          const cnt = link.review_count || 0;
                          const pos = link.positive_count || 0;
                          const crit = link.critical_count || 0;
                          const pctOfTotal = totalReviews > 0 ? Math.round((cnt / totalReviews) * 100) : 0;
                          const avgLinks = links.filter(l => l.review_count > 0);
                          const avgCount = avgLinks.length ? Math.round(avgLinks.reduce((s, l) => s + (l.review_count || 0), 0) / avgLinks.length) : 0;
                          const aboveAvg = avgCount > 0 && cnt > avgCount;
                          return (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] text-muted-foreground">{pctOfTotal}% aller Bewertungen</span>
                              <span className="text-[10px] text-muted-foreground">·</span>
                              <span className={`text-[10px] font-medium ${aboveAvg ? 'text-green-600' : 'text-muted-foreground'}`}>
                                {aboveAvg ? `+${cnt - avgCount} über Ø` : cnt < avgCount ? `${cnt - avgCount} unter Ø` : 'Ø'}
                              </span>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Right: sparkline + stats below */}
                      <div className="shrink-0 flex flex-col items-end gap-0.5">
                        <Sparkline data={link.sparkline || []} labels={link.sparkline_labels || []} />
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Star className="w-2.5 h-2.5 text-yellow-500" />
                          <span>{link.review_count}</span>
                          {link.avg_rating != null && (
                            <span>· {link.avg_rating}/5</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ── Immoverita Tipp ──────────────────────────────────────────── */}
        <div className="mt-8 rounded-xl shadow-sm p-5 flex items-start gap-4"
             style={{
               background: 'linear-gradient(135deg, #f0fdf8 0%, #eff6ff 100%) padding-box, linear-gradient(135deg, rgba(16,185,129,0.3), rgba(59,130,246,0.3)) border-box',
               border: '2px solid transparent',
             }}>
          {/* Icon bubble */}
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
               style={{ background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}>
            <Lightbulb className="w-4 h-4 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-1 block">Immoverita Tipp</span>
            <p className="text-sm font-medium text-slate-600 mb-3 leading-snug">
              Legen Sie für jeden Kanal und jeden Anlass einen eigenen Link an —<br className="hidden sm:block" />
              so sehen Sie genau, woher Ihre Bewertungen kommen. Nach einer Versammlung können Sie einen QR-Code zeigen und Eigentümer direkt um ein Feedback bitten.
            </p>

            {/* Example pills */}
            <div className="flex flex-wrap gap-2">
              {[
                ['QR-Code Anschreiben',  'qr-code-anschreiben'],
                ['QR-Code Flyer',        'qr-code-flyer'],
                ['E-Mail Signatur',      'email-signatur'],
                ['Website',             'website'],
                ['Portalvorgang',        'portalvorgang'],
                ['Versammlung',         'versammlung'],
                ['Beiratstermin',       'beiratstermin'],
                ['Belegprüfung',        'belegpruefung'],
                ['Wohnungsübergabe',    'wohnungsuebergabe'],
              ].map(([use, slug]) => (
                <div key={slug}
                     className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 border border-slate-200 text-xs">
                  <span className="font-medium text-slate-500">{use}</span>
                  <span className="text-slate-300">·</span>
                  <span className="font-mono text-slate-400 text-[11px]">{slug}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Create Dialog ─────────────────────────────────────────────── */}
        <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setSlugEdited(false); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Neuen Bewertungslink erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-3">
              {/* Name */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Name</label>
                <Input
                  value={newLinkName}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="z.B. QR-Code Anschreiben"
                  data-testid="create-link-name-input"
                  className={isDuplicateName(newLinkName) ? 'border-red-400 focus-visible:ring-red-300' : ''}
                />
                {isDuplicateName(newLinkName) && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" /> Ein Link mit diesem Namen existiert bereits.
                  </p>
                )}
              </div>

              {/* Naming tip — tipp card design */}
              <div className="rounded-xl p-4 flex items-start gap-3"
                   style={{
                     background: 'linear-gradient(135deg, #f0fdf8 0%, #eff6ff 100%) padding-box, linear-gradient(135deg, rgba(16,185,129,0.3), rgba(59,130,246,0.3)) border-box',
                     border: '2px solid transparent',
                   }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}>
                  <Lightbulb className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="text-xs text-slate-600">
                  <p className="font-semibold mb-1.5">Tipp: Aussagekräftiger Linkname</p>
                  <p className="opacity-75 mb-1.5">Legen Sie für jeden Kanal einen eigenen Link an — so sehen Sie, woher Ihre Bewertungen kommen:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      ['QR-Code Anschreiben',    'qr-code-anschreiben'],
                      ['QR-Code Flyer',           'qr-code-flyer'],
                      ['E-Mail Signatur',          'email-signatur'],
                      ['Website',                 'website'],
                      ['Portalvorgang',            'portalvorgang'],
                      ['2026 Tag der offenen Tür', '2026-tag-der-offenen-tuer'],
                      ['2026 Messe',              '2026-messe'],
                    ].map(([name, slug]) => (
                      <button
                        key={slug}
                        type="button"
                        onClick={() => { onNameChange(name); setNewLinkSlug(slug); setSlugEdited(true); }}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 border border-slate-200 hover:border-blue-300 hover:bg-white transition-colors text-xs cursor-pointer"
                      >
                        <span className="font-medium text-slate-600">{name}</span>
                        <span className="text-slate-300">·</span>
                        <span className="font-mono text-slate-400 text-[11px]">{slug}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Slug */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Link-URL</label>
                <div className="flex h-9">
                  <span className="inline-flex items-center text-xs text-muted-foreground bg-muted px-2.5 rounded-l-md border border-r-0 border-border whitespace-nowrap h-full">
                    {user?.tenant?.slug}/
                  </span>
                  <Input
                    value={newLinkSlug}
                    onChange={(e) => { setSlugEdited(true); setNewLinkSlug(toSlug(e.target.value)); }}
                    placeholder="qr-code-anschreiben"
                    className="rounded-l-none h-full"
                  />
                </div>
                {(() => {
                  const isDup = isDuplicateName(newLinkName);
                  if (isDup) return null; // duplicate shown above, don't double-hint
                  const { ok, msg } = slugStatus(newLinkSlug);
                  return (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${ok ? 'text-green-600' : 'text-amber-600'}`}>
                      {ok
                        ? <CheckCircle className="w-3.5 h-3.5" />
                        : <AlertCircle className="w-3.5 h-3.5" />}
                      {msg}
                    </p>
                  );
                })()}
              </div>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => { setCreateOpen(false); setSlugEdited(false); }}>Abbrechen</Button>
              <Button
                onClick={handleCreateLink}
                disabled={submitting || !newLinkName.trim() || isDuplicateName(newLinkName) || !slugStatus(newLinkSlug).ok}
                data-testid="create-link-submit-button"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Upsell Dialog (free plan) ─────────────────────────────────── */}
        <Dialog open={upsellOpen} onOpenChange={setUpsellOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Mehrere Links freischalten
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Im kostenlosen Plan ist <strong>1 Bewertungslink</strong> enthalten.
                Mit <strong>Standard</strong> oder höher erhalten Sie unbegrenzte Links — ideal für mehrere Standorte, Zielgruppen oder Kanäle.
              </p>
              <div className="space-y-2">
                {[
                  { plan: 'Standard', price: '€29/Monat', color: 'text-blue-600', items: ['Unbegrenzte Links', 'Logo & Markenfarben', 'KI-Textvorschläge'] },
                  { plan: 'Premium', price: '€79/Monat', color: 'text-violet-600', items: ['Alles aus Standard', 'CSV-Export', 'KI-Sachlichkeitsindex', 'MetaScore'] },
                ].map(p => (
                  <div key={p.plan} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30">
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${p.color}`}>{p.plan} <span className="text-muted-foreground font-normal">— {p.price}</span></p>
                      <ul className="mt-1 space-y-0.5">
                        {p.items.map(i => <li key={i} className="text-xs text-muted-foreground">✓ {i}</li>)}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="secondary" onClick={() => setUpsellOpen(false)}>Schließen</Button>
              <Link to="/app/billing">
                <Button onClick={() => setUpsellOpen(false)}>
                  <Zap className="w-4 h-4 mr-2" />
                  Jetzt upgraden
                </Button>
              </Link>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Edit Dialog ───────────────────────────────────────────────── */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Link umbenennen</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium mb-2 block">Name</label>
              <Input
                value={newLinkName}
                onChange={(e) => setNewLinkName(e.target.value)}
                placeholder="Neuer Name"
              />
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setEditOpen(false)}>Abbrechen</Button>
              <Button onClick={handleUpdateLink} disabled={submitting || !newLinkName.trim()}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      <HVFooter />
      </main>
    </div>
  );
}
